class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length && this.#hasRenderableProducts(recommendations)) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
        } else {
          this.#loadFallbackProducts();
        }
      })
      .catch((e) => {
        this.#loadFallbackProducts().catch(() => this.#handleError(e));
      });
  }

  /**
   * Check whether the rendered recommendation HTML contains visible product content.
   * @param {Element} recommendations
   * @returns {boolean}
   */
  #hasRenderableProducts(recommendations) {
    return Boolean(
      recommendations.querySelector('product-card, product-card-link, .he-recommendation-card, a[href*="/products/"] img')
    );
  }

  /**
   * Fallback for cases where Shopify returns empty recommendation items.
   */
  async #loadFallbackProducts() {
    const limit = Number(new URL(this.dataset.url, window.location.origin).searchParams.get('limit')) || 5;
    const response = await fetch(`/products.json?limit=${limit + 1}`, { credentials: 'same-origin' });

    if (!response.ok) {
      throw new Error(`Fallback products returned ${response.status}`);
    }

    const data = await response.json();
    const productId = String(this.dataset.productId || '');
    const products = (data.products || [])
      .filter((product) => String(product.id) !== productId)
      .slice(0, limit);

    if (!products.length) {
      this.#handleError(new Error('No fallback products available'));
      return;
    }

    this.#ensureFallbackStyles();

    const list = this.querySelector('.resource-list');
    if (!list) {
      this.#handleError(new Error('Recommendation list not found'));
      return;
    }

    list.innerHTML = products
      .map((product) => {
        const image = product.images?.[0]?.src || '';
        const variant = product.variants?.[0] || {};
        const price = this.#formatMoney(variant.price);
        const compareAtPrice = variant.compare_at_price ? this.#formatMoney(variant.compare_at_price) : '';

        return `
          <div class="resource-list__item">
            <a class="he-recommendation-card" href="/products/${product.handle}">
              <div class="he-recommendation-card__media">
                ${image ? `<img src="${image}" alt="${this.#escapeHtml(product.title)}" loading="lazy">` : ''}
              </div>
              <div class="he-recommendation-card__info">
                <div class="he-recommendation-card__title">${this.#escapeHtml(product.title)}</div>
                <div class="he-recommendation-card__price">
                  <span>${price}</span>
                  ${compareAtPrice ? `<s>${compareAtPrice}</s>` : ''}
                </div>
              </div>
            </a>
          </div>
        `;
      })
      .join('');

    this.dataset.recommendationsPerformed = 'true';
    this.classList.remove('hidden');
  }

  /**
   * @param {string | number | undefined} value
   * @returns {string}
   */
  #formatMoney(value) {
    const number = Number(value || 0);
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
    }).format(number);
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value || '';
    return div.innerHTML;
  }

  #ensureFallbackStyles() {
    if (document.getElementById('he-recommendation-card-fallback-styles')) return;

    const style = document.createElement('style');
    style.id = 'he-recommendation-card-fallback-styles';
    style.textContent = `
      .he-recommendation-card {
        display: block;
        color: inherit;
        text-decoration: none;
      }
      .he-recommendation-card__media {
        aspect-ratio: 3 / 4;
        background: #f3f4f5;
        overflow: hidden;
      }
      .he-recommendation-card__media img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }
      .he-recommendation-card__info {
        padding-top: 12px;
        font-size: 13px;
        line-height: 1.35;
      }
      .he-recommendation-card__title {
        font-weight: 600;
        margin-bottom: 6px;
      }
      .he-recommendation-card__price {
        display: flex;
        gap: 8px;
        align-items: baseline;
      }
      .he-recommendation-card__price s {
        color: rgba(0, 0, 0, 0.45);
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
